import React, { useEffect, useState } from "react";
import { fmtDateTime } from "@/lib/dates";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, Check } from "lucide-react";

const FIELDS = [
  ["name", "Name"], ["title", "Title"], ["email", "Contact email shown to visitors"],
  ["phone", "Phone shown to visitors"], ["notify_email", "Send new questions to (your inbox)"],
];

function AdvisorProfile() {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/advisor").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return null;

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/admin/advisor", a); setA(data); toast.success("Advisor details saved"); }
    catch (err) { toast.error(apiErr(err?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { const { data } = await api.post("/admin/advisor/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setA(data); toast.success("Photo updated"); }
    catch (err) { toast.error(apiErr(err?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-xl border p-5 grid md:grid-cols-[160px_1fr] gap-6" data-testid="advisor-profile-form">
      <div className="text-center">
        {a.photo_url ? <img src={a.photo_url} alt={a.name} className="h-32 w-32 rounded-full object-cover mx-auto ring-4 ring-cream" data-testid="advisor-admin-photo" />
          : <div className="h-32 w-32 rounded-full bg-coral text-white grid place-items-center text-4xl font-bold mx-auto">{a.name?.[0]}</div>}
        <label className="mt-3 inline-flex items-center gap-1 text-xs text-brand underline cursor-pointer">
          <Upload className="h-3 w-3" />Change photo
          <input data-testid="advisor-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      <div className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k}><Label className="text-xs">{label}</Label><Input data-testid={`advisor-field-${k}`} value={a[k] || ""} onChange={(e) => setA({ ...a, [k]: e.target.value })} /></div>
          ))}
        </div>
        <div><Label className="text-xs">Intro message</Label><Textarea data-testid="advisor-field-intro" rows={2} value={a.intro || ""} onChange={(e) => setA({ ...a, intro: e.target.value })} /></div>
        <Button data-testid="advisor-save-btn" type="submit" disabled={busy} className="rounded-full bg-coral text-white">{busy ? "Saving…" : "Save changes"}</Button>
      </div>
    </form>
  );
}

function Questions() {
  const [qs, setQs] = useState([]);
  const load = () => api.get("/admin/advisor/questions").then((r) => setQs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const mark = async (q, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.post(`/admin/advisor/questions/${q.id}/status`, fd); load();
  };
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <Table>
        <TableHeader><TableRow><TableHead>When</TableHead><TableHead>From</TableHead><TableHead>Question</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {qs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8" data-testid="advisor-questions-empty">No questions yet.</TableCell></TableRow>}
          {qs.map((q) => (
            <TableRow key={q.id} data-testid={`advisor-question-${q.id}`}>
              <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(q.created_at)}</TableCell>
              <TableCell className="text-sm"><div className="font-medium">{q.name}</div><a href={`mailto:${q.email}`} className="text-xs underline text-brand">{q.email}</a></TableCell>
              <TableCell className="text-sm max-w-md whitespace-pre-wrap">{q.question}</TableCell>
              <TableCell><Badge variant={q.status === "answered" ? "secondary" : "default"}>{q.status}</Badge></TableCell>
              <TableCell>{q.status !== "answered" && <Button size="sm" variant="ghost" data-testid={`advisor-answered-${q.id}`} onClick={() => mark(q, "answered")} className="h-7 text-xs"><Check className="h-3 w-3 mr-1" />Answered</Button>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AdvisorAdmin() {
  return (
    <div className="space-y-6">
      <AdvisorProfile />
      <div><h3 className="font-semibold text-brand mb-2">Questions received</h3><Questions /></div>
    </div>
  );
}
